/** Small 3D math helpers for the dependency-free molecule renderer. */

export type Vec3 = [number, number, number];
/** Row-major 3×3 rotation matrix */
export type Mat3 = [number, number, number, number, number, number, number, number, number];

export const IDENTITY: Mat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1];

export function multiply(a: Mat3, b: Mat3): Mat3 {
  const r = new Array(9).fill(0) as Mat3;
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      for (let k = 0; k < 3; k++) r[i * 3 + j] += a[i * 3 + k] * b[k * 3 + j];
  return r;
}

export function rotationX(t: number): Mat3 {
  const c = Math.cos(t), s = Math.sin(t);
  return [1, 0, 0, 0, c, -s, 0, s, c];
}

export function rotationY(t: number): Mat3 {
  const c = Math.cos(t), s = Math.sin(t);
  return [c, 0, s, 0, 1, 0, -s, 0, c];
}

export function apply(m: Mat3, v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

/** Rotate by screen-space drag: horizontal drag spins around Y, vertical around X (applied in view space). */
export function dragRotate(m: Mat3, dx: number, dy: number, sensitivity = 0.01): Mat3 {
  return multiply(multiply(rotationX(dy * sensitivity), rotationY(dx * sensitivity)), m);
}

export interface Projected { x: number; y: number; z: number; scale: number }

/** Weak perspective projection. `camera` is the eye distance in model units. y grows downward on screen. */
export function project(v: Vec3, cx: number, cy: number, pxPerUnit: number, camera = 9): Projected {
  const scale = camera / (camera - v[2]);
  return { x: cx + v[0] * pxPerUnit * scale, y: cy - v[1] * pxPerUnit * scale, z: v[2], scale };
}

export function isOrthonormal(m: Mat3, tol = 1e-9): boolean {
  const t = multiply(m, [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]);
  return IDENTITY.every((v, i) => Math.abs(v - t[i]) < tol);
}
